import { createApi } from '@reduxjs/toolkit/query/react';

import { User } from '../types/user-types';
import { baseQueryWithErrorHandling } from './baseQuery';

interface UserCredentials {
  email?: string;
  password1: string;
  password2?: string;
  first_name?: string;
  last_name?: string;
  token?: string;
  phone_number?: string;
}

interface LoginCredentials {
  username: string;
  password: string;
  // Optional auth secret for front plugin
  auth_secret?: string;
}

export interface UserLoginResponse {
  success: string;
  session_id: string;
  user: User;
}

export const userApiClient = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    signupUser: builder.mutation<UserLoginResponse, UserCredentials>({
      query: (credentials) => ({
        url: 'api/signup/',
        method: 'POST',
        body: credentials,
        headers: {
          // Add your headers here
        },
      }),
    }),
    loginUser: builder.mutation<UserLoginResponse, LoginCredentials>({
      query: ({ username, password, auth_secret }) => ({
        url: auth_secret
          ? `api/login/?auth_secret=${auth_secret}`
          : 'api/login/',
        method: 'POST',
        body: {
          username,
          password,
        },
      }),
    }),
    checkSession: builder.query<
      { is_valid: boolean; email: string; user: User },
      void
    >({
      query: () => ({
        url: 'api/check-session/',
        method: 'GET',
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: 'api/logout/',
        method: 'POST',
        credentials: 'include',
      }),
    }),
    requestResetPassword: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: 'api/request-reset-password/',
        method: 'POST',
        body: { email },
      }),
    }),
    resetPassword: builder.mutation<void, { password: string; token: string }>({
      query: ({ password, token }) => ({
        url: 'api/reset-password/',
        method: 'POST',
        body: { password, token },
      }),
    }),
    getStaffUsers: builder.query<User[], void>({
      query: () => ({
        url: 'api/users/staff/',
        method: 'GET',
      }),
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: 'api/user/',
        method: 'GET',
      }),
    }),
    getUserById: builder.query<User, string>({
      query: (userId) => ({
        url: `api/users/${userId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),
    uploadUserAvatar: builder.mutation<User, { userId: string; file: File }>({
      query: ({ userId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `api/users/${userId}/avatar/`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    deleteUserAvatar: builder.mutation<void, string>({
      query: (userId) => ({
        url: `api/users/${userId}/avatar/`,
        method: 'DELETE',
      }),
    }),
    uploadUserSignature: builder.mutation<User, { userId: string; file: File }>(
      {
        query: ({ userId, file }) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `api/users/${userId}/signature/`,
            method: 'POST',
            body: formData,
          };
        },
      },
    ),
    deleteUserSignature: builder.mutation<void, string>({
      query: (userId) => ({
        url: `api/users/${userId}/signature/`,
        method: 'DELETE',
      }),
    }),
    updateUserExtra: builder.mutation<User, { userId: string; extra: object }>({
      query: ({ userId, extra }) => ({
        url: `api/users/${userId}/`,
        method: 'PATCH',
        body: { extra },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
      ],
    }),
  }),
});

export const {
  useSignupUserMutation,
  useLoginUserMutation,
  useCheckSessionQuery,
  useLogoutMutation,
  useRequestResetPasswordMutation,
  useResetPasswordMutation,
  useGetStaffUsersQuery,
  useGetCurrentUserQuery,
  useGetUserByIdQuery,
  useUploadUserAvatarMutation,
  useDeleteUserAvatarMutation,
  useUploadUserSignatureMutation,
  useDeleteUserSignatureMutation,
  useUpdateUserExtraMutation,
} = userApiClient;
